import type { ClubeLivre, Comando, EstadoPedido, InfoCampeonato, ResumoSala, RespostaComando, VisaoSala } from '../../server/sala';
import { aplicarDiferenca, HIDRATACAO_MINUTO, PAISES_MUNDO, clubePorId, lanceMinimo, tabela, temCopaJunta, type EstadoJogo, type NoDiferenca } from '../core';
import { app, temporadaAcabou, erroTexto, humano, jogoAtual } from './app';
import { formatarRestante } from './abas';
import { aplicarEscalar } from './escalar';
import { formacaoParaEnvio, iniciarPausa } from './pausa';
import { dialogo } from './dialogos';
import { chatNovaVisao } from './chat';
import { agoraDoServidor, registrarAmostra, reiniciarRelogioServidor } from './relogio';
import { escudo, logoCampeonato } from './icones';
import { esc } from './util';

/**
 * Cliente do campeonato online. O servidor é a fonte da verdade: cada ação vira um comando enviado a ele e o estado do jogo
 * é substituído pela resposta. A tela de jogo é a mesma do modo solo (app.jogo passa a ser a cópia vinda do servidor).
 */

interface Ganchos {
  desenhar: () => void;
  exibirRodada: () => void;
}

let ganchos: Ganchos = { desenhar: () => undefined, exibirRodada: () => undefined };
const CHAVE = 'novo-jogo:online:v1';

const CHAVE_CONVITE = 'webfoot:convite';

/** Código do link de convite que trouxe a pessoa (guardado até ela criar a conta). */
function conviteGuardado(): string {
  try {
    return localStorage.getItem(CHAVE_CONVITE) ?? '';
  } catch {
    return '';
  }
}

/** Link com ?convite=CODIGO: guarda o código, limpa o endereço e abre a janela da conta para criar o cadastro. */
function lerConviteDaUrl(): void {
  const params = new URLSearchParams(location.search);
  const cod = (params.get('convite') ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
  if (!cod) return;
  try {
    if (!sessaoDaConta()) localStorage.setItem(CHAVE_CONVITE, cod);
  } catch {
    /* sem armazenamento: o código vale só se a conta for criada nesta mesma aba */
  }
  params.delete('convite');
  const resto = params.toString();
  history.replaceState(null, '', location.pathname + (resto ? `?${resto}` : '') + location.hash);
  if (!sessaoDaConta()) {
    prefillConvite = cod;
    app.dialogo = 'conta';
    app.mensagem = 'Você foi convidado! Crie sua conta para o técnico que te convidou ganhar WebCoins.';
  }
}
let prefillConvite = '';

export function iniciarOnline(g: Ganchos): void {
  ganchos = g;
  void carregarConfigConta();
  lerConviteDaUrl();
  const sala = new URLSearchParams(location.search).get('sala');
  if (sala) {
    app.tela = 'online';
    prefill.codigo = sala.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
    aba = 'entrar'; // veio por um link de sala
    void carregarCampeonatos();
  } else if (lerSessao() && conta) {
    acaoOnline('online-reconectar'); // sem conta logada não reconecta sozinho: o online exige login
  }
}

/** "Campeão do Brasileirão: X · campeão da Copa do Brasil: Y" (só o que já está definido). */
function textoCampeoes(e: EstadoJogo): string {
  const nome = (id: number | null | undefined): string | null => (id == null ? null : clubePorId(e, id).nome);
  const copa = nome(e.copa?.campeaoId);
  if (e.formato === 'copa') return copa ? `campeão: ${copa}` : '';
  const liga = temporadaAcabou(e) ? nome(tabela(e)[0]?.clubeId) : null;
  const partes = [liga ? `campeão do ${e.divisao === 'b' ? 'Brasileirão Série B' : 'Brasileirão'}: ${liga}` : '', temCopaJunta(e) && copa ? `campeão da Copa do Brasil: ${copa}` : ''].filter(Boolean);
  return partes.join(' · ');
}

const prefill = { codigo: '', nome: '', prazo: '0', campeonato: 'brasileirao-copa', prazoPers: '30', prazoUnidade: 'min', senhaCriar: '', senhaEntrar: '', maxTecnicos: '10' };

// ---- lista de salas e entrada no meio do campeonato ------------------------------------------------

/** Aba aberta na tela do online: criar/entrar numa sala ou ver as salas existentes. */
let aba: 'criar' | 'entrar' | 'salas' | 'conta' = 'criar';
let salasPublicas: ResumoSala[] | null = null;
let atualizadorSalas: number | undefined;

/** Sala já iniciada: o técnico novo escolhe um clube da CPU e pede ao anfitrião. */
let tardia: { codigo: string; nome: string; senha: string; clubes: ClubeLivre[] } | null = null;
/** Pedido enviado, esperando a resposta do anfitrião. */
let espera: { codigo: string; pedido: string; clube: string; timer: number } | null = null;

async function carregarSalas(): Promise<void> {
  try {
    salasPublicas = await chamar<ResumoSala[]>('/salas');
  } catch {
    salasPublicas = salasPublicas ?? [];
  }
}

function pararAtualizadorSalas(): void {
  window.clearInterval(atualizadorSalas);
  atualizadorSalas = undefined;
}

/** Enquanto a aba Salas está aberta, a lista se atualiza sozinha a cada 5 segundos. */
function ligarAtualizadorSalas(): void {
  pararAtualizadorSalas();
  atualizadorSalas = window.setInterval(() => {
    if (app.tela !== 'online' || aba !== 'salas' || digitando()) return pararAtualizadorSalas();
    void carregarSalas().then(() => ganchos.desenhar());
  }, 5000);
}

function pararEspera(): void {
  if (espera) window.clearInterval(espera.timer);
  espera = null;
}

/** Depois de pedir um clube, pergunta ao servidor a cada 2 s o que o anfitrião respondeu. */
function acompanharPedido(codigo: string, pedido: string, clube: string): void {
  pararEspera();
  const timer = window.setInterval(async () => {
    try {
      const r = await chamar<EstadoPedido>(`/salas/${encodeURIComponent(codigo)}/pedido?token=${encodeURIComponent(pedido)}`);
      if (r.estado === 'aguardando' || !espera) return;
      pararEspera();
      if (r.estado === 'aprovado') {
        await conectar(codigo, r.token);
        ganchos.desenhar();
        return;
      }
      app.mensagem = r.estado === 'recusado' ? `O anfitrião não aceitou: ${r.motivo}.` : 'O pedido expirou. Peça de novo.';
      ganchos.desenhar();
    } catch {
      /* sem rede agora: tenta no próximo ciclo */
    }
  }, 2000);
  espera = { codigo, pedido, clube, timer };
}

/**
 * Mudança num campo da tela de entrada que exige redesenhar (campeonato: descrição; prazo: campo personalizado). O que a pessoa já
 * digitou no nome e no código é guardado antes, senão o redesenho apagaria. Devolve true se a tela deve ser redesenhada.
 */
/** Guarda o que está digitado na tela de entrada. Cada aba só tem alguns campos: os que não estão na tela ficam como estavam. */
function guardarCamposDaTela(): void {
  const ler = (id: string, atual: string): string => (document.getElementById(id) ? campo(id) : atual);
  prefill.nome = ler('online-nome', prefill.nome);
  prefill.codigo = ler('online-codigo', prefill.codigo).toUpperCase();
  prefill.senhaCriar = ler('online-senha-criar', prefill.senhaCriar);
  prefill.maxTecnicos = ler('online-max', prefill.maxTecnicos);
  prefill.senhaEntrar = ler('online-senha', prefill.senhaEntrar);
}

export function mudarEntrada(chave: string, valor: string): boolean {
  if (chave === 'online-pers') {
    prefill.prazoPers = valor;
    return false;
  }
  if (chave === 'online-max') {
    prefill.maxTecnicos = valor; // só guarda: redesenhar a tela devolveria o seletor ao valor antigo
    return false;
  }
  guardarCamposDaTela();
  if (chave === 'online-camp') prefill.campeonato = valor;
  else if (chave === 'online-prazo') prefill.prazo = valor;
  else if (chave === 'online-unid') prefill.prazoUnidade = valor === 'h' ? 'h' : 'min';
  return true;
}

/** Prazo escolhido em minutos (0 = sem prazo); null se o prazo personalizado for inválido (1 minuto a 24 horas). */
function prazoEmMinutos(): number | null {
  if (prefill.prazo !== 'custom') return Number(prefill.prazo) || 0;
  const n = Number(campo('online-pers').replace(',', '.'));
  const min = Math.round(prefill.prazoUnidade === 'h' ? n * 60 : n);
  return Number.isFinite(min) && min >= 1 && min <= 1440 ? min : null;
}
let campeonatos: InfoCampeonato[] = [];

async function carregarCampeonatos(): Promise<void> {
  try {
    campeonatos = await chamar<InfoCampeonato[]>('/campeonatos');
  } catch {
    campeonatos = [];
  }
}

function lerSessao(): { codigo: string; token: string } | null {
  try {
    const t = JSON.parse(localStorage.getItem(CHAVE) ?? 'null') as { codigo?: string; token?: string } | null;
    return t?.codigo && t.token ? { codigo: t.codigo, token: t.token } : null;
  } catch {
    return null;
  }
}

function gravarSessao(s: { codigo: string; token: string } | null): void {
  try {
    if (s) localStorage.setItem(CHAVE, JSON.stringify(s));
    else localStorage.removeItem(CHAVE);
  } catch {
    /* sem armazenamento: a sala continua valendo até fechar a aba */
  }
}

export async function chamar<T>(caminho: string, corpo?: unknown): Promise<T> {
  const r = await fetch(`./api${caminho}`, {
    method: corpo === undefined ? 'GET' : 'POST',
    headers: corpo === undefined ? undefined : { 'content-type': 'application/json' },
    body: corpo === undefined ? undefined : JSON.stringify(corpo),
  });
  let dados: unknown = null;
  try {
    dados = await r.json();
  } catch {
    /* resposta sem JSON */
  }
  if (!r.ok) throw new Error((dados as { erro?: string } | null)?.erro ?? `falha ${r.status}`);
  return dados as T;
}

// ---- conexão -------------------------------------------------------------------------------------

function digitando(): boolean {
  const el = document.activeElement;
  if (el?.closest('#chat')) return false; // digitar no bate-papo não impede o jogo de atualizar
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA');
}

/** Convites por desempenho que o técnico já viu (para a janela abrir sozinha só uma vez por conjunto). */
let conviteVisto = '';

function aplicarVisao(v: VisaoSala): void {
  const o = app.online;
  if (!o) return;
  o.visao = v;
  o.recebidoEm = Date.now();
  registrarAmostra(v.sala.agora, o.recebidoEm);
  chatNovaVisao(v);
  if (!v.sala.iniciada) {
    app.tela = 'lobby';
    return;
  }
  app.jogo = v.estado;
  const e = v.estado;
  const marca = e.temporada * 1000 + e.rodada;
  const avancou = o.rodadaVista !== null && marca > o.rodadaVista && e.rodada > 0 && !!e.ultimaRodada;
  o.rodadaVista = marca;
  if (app.tela === 'lobby' || app.tela === 'online') {
    app.tela = 'jogo';
    app.jogadorSel = null;
    app.dialogo = null;
  }
  if (avancou && app.tela !== 'partida') {
    app.escalar = null;
    ganchos.exibirRodada();
  } else if (v.sala.aoVivo && app.tela === 'jogo' && !app.partida && e.ultimaRodada) {
    // entrou (ou reconectou) com a rodada em andamento: volta para a partida
    ganchos.exibirRodada();
  }

  // partida ao vivo: se alguém mexeu no time (pausa), os resultados mudaram desde o minuto da pausa
  // (só se ainda é a mesma rodada: terminada a do técnico, o servidor pode já ter jogado as seguintes, sem ele, e a tela dele continua na dele)
  if (app.partida?.online && e.ultimaRodada && e.ultimaRodada.rodada === app.partida.rodada.rodada) app.partida.rodada = e.ultimaRodada;
  const pausa = v.sala.aoVivo?.pausa ?? null;
  const iv = v.sala.aoVivo?.intervalo;
  const intervaloMeu = !!iv && !iv.feito && iv.ate !== null && v.eu.clubeId !== null && iv.esperados.includes(v.eu.clubeId) && !iv.prontos.includes(v.eu.clubeId);
  // convites novos por desempenho (fim da temporada): abre a janela uma vez para cada conjunto de convites
  const conviteKey = (v.eu.convites ?? []).map((c) => c.ofertaId).join(',');
  if (conviteKey && conviteKey !== conviteVisto && !v.sala.aoVivo && app.tela === 'jogo' && !app.dialogo) {
    app.dialogo = 'convites';
    app.mensagem = '';
  }
  if (!v.sala.aoVivo) conviteVisto = conviteKey;
  const hid = v.sala.aoVivo?.hidratacao;
  const hidratacaoMeu = !!hid && !hid.feito && hid.ate !== null && !(iv && !iv.feito) && v.eu.clubeId !== null && hid.esperados.includes(v.eu.clubeId) && !hid.prontos.includes(v.eu.clubeId);
  if (intervaloMeu && iv && iv.ate !== null) {
    // intervalo: o jogo parou aos 45' e todos os técnicos escalam o 2º tempo
    if (app.pausa?.chave !== `iv:45:${iv.ate}`) iniciarPausa(45, iv.ate, true);
  } else if (hidratacaoMeu && hid && hid.ate !== null) {
    // pausa para hidratação: o jogo parou aos 65' para todos, que podem ajustar o time
    if (app.pausa?.chave !== `hid:${HIDRATACAO_MINUTO}:${hid.ate}`) iniciarPausa(HIDRATACAO_MINUTO, hid.ate, true, true);
  } else if (pausa && pausa.clubeId === v.eu.clubeId) {
    if (app.pausa?.chave !== `p:${pausa.minuto}:${pausa.ate}`) iniciarPausa(pausa.minuto, pausa.ate);
  } else if (app.dialogo === 'pausa') {
    app.dialogo = null;
    app.pausa = null;
  }
}

/** Resposta do servidor: a visão inteira (com `vid`) ou só a diferença desde a última que este navegador recebeu. */
type RespostaVisao = (VisaoSala & { vid?: number }) | { delta: { de: number; vid: number; patch: NoDiferenca } };

/**
 * Aplica a resposta e devolve true; false = veio uma diferença que não combina com o que temos (pede a visão inteira de novo).
 * `o.base` guarda a cópia intacta da última visão; a tela recebe uma cópia dela (o desenho pode mexer no objeto).
 */
function receberVisao(resposta: RespostaVisao): boolean {
  const o = app.online;
  if (!o) return true;
  if ('delta' in resposta) {
    const d = resposta.delta;
    if (o.vid === d.de && o.base) {
      aplicarDiferenca(o.base, d.patch);
      o.vid = d.vid;
      aplicarVisao(structuredClone(o.base) as VisaoSala);
      return true;
    }
    return o.vid !== undefined && o.vid !== null && o.vid >= d.vid; // já temos algo igual ou mais novo
  }
  const { vid, ...visao } = resposta;
  if (vid !== undefined) {
    if (o.vid !== undefined && o.vid !== null && vid < o.vid) return true; // resposta atrasada
    o.vid = vid;
    o.base = structuredClone(visao);
  } else {
    o.vid = null;
    o.base = null;
  }
  aplicarVisao(visao as VisaoSala);
  return true;
}

/** Parâmetro com a versão que já temos, para o servidor mandar só o que mudou. */
function baseParam(o: NonNullable<typeof app.online>): string {
  return o.vid !== undefined && o.vid !== null && o.base ? `&base=${o.vid}` : '';
}

async function atualizar(): Promise<void> {
  const o = app.online;
  if (!o) return;
  try {
    const url = `/salas/${o.codigo}?token=${encodeURIComponent(o.token)}`;
    const r = await chamar<RespostaVisao>(url + baseParam(o));
    if (!receberVisao(r)) receberVisao(await chamar<RespostaVisao>(url));
  } catch (erro) {
    app.mensagem = `Sem conexão com o servidor: ${erroTexto(erro)}`;
  }
  if (!digitando()) ganchos.desenhar();
}

function abrirFonte(): void {
  const o = app.online;
  if (!o) return;
  o.fonte?.close();
  const fonte = new EventSource(`./api/salas/${o.codigo}/eventos?token=${encodeURIComponent(o.token)}`);
  fonte.onmessage = () => void atualizar();
  o.fonte = fonte;
  // reserva: alguns túneis seguram o aviso em tempo real; a cada 1,5 s pergunta só o número da versão (resposta minúscula)
  window.clearInterval(checagem);
  checagem = window.setInterval(async () => {
    const atual = app.online;
    if (!atual) return;
    try {
      const r = await chamar<{ versao: number }>(`/salas/${atual.codigo}/versao?token=${encodeURIComponent(atual.token)}`);
      if (app.online === atual && r.versao !== atual.visao?.sala.versao) await atualizar();
    } catch {
      /* sem rede agora: tenta de novo no próximo ciclo */
    }
  }, 1500);
}

let checagem: number | undefined;

async function conectar(codigo: string, token: string): Promise<void> {
  gravarSessao({ codigo, token });
  reiniciarRelogioServidor();
  app.online = { codigo, token, visao: null, fonte: null, rodadaVista: null, recebidoEm: Date.now() };
  receberVisao(await chamar<RespostaVisao>(`/salas/${codigo}?token=${encodeURIComponent(token)}`));
  abrirFonte();
}

/** Deixa a sala sem apagar a sessão salva (dá para reconectar depois). Usado ao começar um jogo solo. */
export function desconectarOnline(): void {
  window.clearInterval(checagem);
  app.online?.fonte?.close();
  app.online = null;
}

export function sairOnline(): void {
  window.clearInterval(checagem);
  app.online?.fonte?.close();
  app.online = null;
  gravarSessao(null);
  app.jogo = null;
  app.dialogo = null;
  app.partida = null;
  app.tela = 'inicio';
  try {
    history.replaceState(null, '', location.pathname);
  } catch {
    /* ignorar */
  }
}

function falha(erro: unknown): void {
  app.mensagem = erroTexto(erro);
  ganchos.desenhar();
}

// ---- ações da entrada e do lobby -----------------------------------------------------------------

function campo(id: string): string {
  return (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null)?.value.trim() ?? '';
}

// ---- conta (usuário e senha no servidor) ---------------------------------------------------------

const CHAVE_CONTA = 'novo-jogo:conta:v1';

interface SalaDaConta {
  codigo: string;
  token: string;
  nome: string;
  campeonato: string;
  iniciada: boolean;
  clubeId: number | null;
}

let conta: { token: string; usuario: string } | null = (() => {
  try {
    return JSON.parse(localStorage.getItem(CHAVE_CONTA) ?? 'null') as { token: string; usuario: string } | null;
  } catch {
    return null;
  }
})();
let salasDaConta: SalaDaConta[] = [];
let temSaveNaConta = false;

function gravarConta(c: { token: string; usuario: string } | null): void {
  conta = c;
  try {
    if (c) localStorage.setItem(CHAVE_CONTA, JSON.stringify(c));
    else localStorage.removeItem(CHAVE_CONTA);
  } catch {
    /* sem armazenamento: vale só nesta aba */
  }
}

async function carregarConta(): Promise<void> {
  if (!conta) {
    salasDaConta = [];
    return;
  }
  try {
    const r = await chamar<{ usuario: string; temSave: boolean; salas: SalaDaConta[] }>(`/conta?token=${encodeURIComponent(conta.token)}`);
    salasDaConta = r.salas;
    temSaveNaConta = r.temSave;
  } catch (erro) {
    if (/sessão/i.test(erroTexto(erro))) gravarConta(null);
    salasDaConta = [];
  }
}

/** A conta logada neste navegador (token da sessão e usuário), ou null. */
export function sessaoDaConta(): { token: string; usuario: string } | null {
  return conta;
}

export const chamarApi = chamar;

// ---- confirmação de e-mail (código de 6 dígitos) ----------------------------------------------------------
// O servidor decide se exige (ver `exigirEmail` em server/email.ts). Ligada: "Criar conta" pede o e-mail e, em vez de entrar, abre
// a etapa do código; quem tenta entrar numa conta ainda não confirmada cai na mesma etapa.

let exigeEmail = false;
let confirmacao: { usuario: string; senha: string; email: string } | null = null;

/** O servidor exige e-mail confirmado para criar contas? */
export const contaExigeEmail = (): boolean => exigeEmail;
/** Conta criada (ou tentando entrar) que ainda espera o código de 6 dígitos, ou null. */
export const confirmacaoDaConta = (): { usuario: string; senha: string; email: string } | null => confirmacao;

/** Pergunta ao servidor se a confirmação de e-mail está ligada (falha de rede = desligada). */
export async function carregarConfigConta(): Promise<void> {
  try {
    exigeEmail = Boolean((await chamar<{ exigirEmail: boolean }>('/conta/config')).exigirEmail);
  } catch {
    exigeEmail = false;
  }
  ganchos.desenhar();
}

export function cancelarConfirmacao(): void {
  confirmacao = null;
}

function guardarConvite(): string {
  return prefillConvite || conviteGuardado();
}

function limparConvite(): void {
  prefillConvite = '';
  try {
    localStorage.removeItem(CHAVE_CONVITE);
  } catch {
    /* ignora */
  }
}

/**
 * Entra (ou cria) a conta e guarda a sessão; quem chama trata o erro. Devolve 'confirmar' quando falta digitar o código enviado por
 * e-mail (conta nova, ou conta que nunca foi confirmada).
 */
export async function entrarNaConta(rota: 'entrar' | 'registrar', usuario: string, senha: string, email = ''): Promise<'entrou' | 'confirmar'> {
  const convite = rota === 'registrar' ? guardarConvite() : '';
  let r: { token?: string; usuario: string; pendente?: boolean; email?: string };
  try {
    r = await chamar(`/conta/${rota}`, { usuario, senha, ...(convite ? { convite } : {}), ...(rota === 'registrar' && email ? { email } : {}) });
  } catch (erro) {
    if (/confirme seu e-?mail/i.test(erroTexto(erro))) confirmacao = { usuario, senha, email: '' };
    throw erro;
  }
  if (r.pendente) {
    confirmacao = { usuario: r.usuario, senha, email: r.email ?? '' };
    if (convite) limparConvite(); // o servidor já guardou o convite no cadastro pendente
    return 'confirmar';
  }
  if (convite) limparConvite();
  confirmacao = null;
  gravarConta(r as { token: string; usuario: string });
  if (!prefill.nome) prefill.nome = r.usuario;
  await carregarConta();
  return 'entrou';
}

/** Manda o código de 6 dígitos digitado; se estiver certo, a conta entra. */
export async function confirmarEmailDaConta(codigo: string): Promise<void> {
  if (!confirmacao) return;
  const r = await chamar<{ token: string; usuario: string }>('/conta/verificar', { usuario: confirmacao.usuario, codigo });
  confirmacao = null;
  gravarConta(r);
  if (!prefill.nome) prefill.nome = r.usuario;
  await carregarConta();
}

/** Pede outro código por e-mail. */
export async function reenviarCodigoDaConta(): Promise<string> {
  if (!confirmacao) return '';
  const r = await chamar<{ email: string }>('/conta/reenviar', { usuario: confirmacao.usuario, senha: confirmacao.senha });
  confirmacao.email = r.email;
  return r.email;
}

/** Etapa do código (aparece no lugar do formulário de entrar/criar conta). */
export function htmlConfirmacaoEmail(): string {
  const c = confirmacao;
  if (!c) return '';
  return `<div class="conta-confirmar"><b>Confirme seu e-mail</b>
    <p class="cinza peq">${c.email ? `Enviamos um código de 6 dígitos para <b>${esc(c.email)}</b>.` : 'Enviamos um código de 6 dígitos para o e-mail da conta.'} Ele vale por 15 minutos (confira o spam).</p>
    <div class="conta-linha"><input type="text" id="cx-codigo" inputmode="numeric" maxlength="6" placeholder="000000" autocomplete="one-time-code" />
      <button class="btn principal" data-a="conta-email-confirmar">Confirmar</button>
      <button class="btn" data-a="conta-email-reenviar">Reenviar código</button>
      <button class="btn" data-a="conta-email-cancelar">Voltar</button></div></div>`;
}

/** Campo de e-mail do cadastro (só aparece quando o servidor exige confirmação). */
export function htmlCampoEmail(id: string): string {
  return exigeEmail ? `<input type="email" id="${id}" maxlength="120" placeholder="e-mail (para confirmar a conta)" autocomplete="email" />` : '';
}

/** Sai da conta neste navegador e encerra a sessão no servidor. */
export function sairDaConta(): void {
  const antigo = conta;
  gravarConta(null);
  salasDaConta = [];
  if (antigo) void chamar('/conta/sair', { token: antigo.token }).catch(() => undefined);
}

/** Dados da conta para outras telas (Opções mostra salvar/carregar na conta). */
export function usuarioDaConta(): { usuario: string; temSave: boolean } | null {
  return conta ? { usuario: conta.usuario, temSave: temSaveNaConta } : null;
}

function blocoConta(): string {
  if (!conta) {
    if (confirmacao) return `<div class="conta-box">${htmlConfirmacaoEmail()}</div>`;
    return `<div class="conta-box"><b>Conta</b> <span class="cinza peq">(guarda suas salas e o seu save em qualquer aparelho)</span>
      <div class="conta-linha"><input type="text" id="conta-usuario" maxlength="24" placeholder="usuário" autocomplete="username" /><input type="password" id="conta-senha" maxlength="100" placeholder="senha (mín. 6)" autocomplete="current-password" />${htmlCampoEmail('conta-email')}
      <button class="btn peq" data-a="online-conta-entrar">Entrar</button><button class="btn peq principal" data-a="online-conta-registrar">Criar conta</button></div></div>`;
  }
  const salas = salasDaConta
    .map((s) => `<li><b>${esc(s.codigo)}</b> · ${esc(s.campeonato)} · ${esc(s.nome)} <button class="btn peq" data-a="online-conta-sala" data-codigo="${esc(s.codigo)}" data-token="${esc(s.token)}">Voltar para a sala</button></li>`)
    .join('');
  return `<div class="conta-box"><b>Conta: ${esc(conta.usuario)}</b> <button class="btn peq" data-a="online-conta-sair">Sair da conta</button>
    <ul class="conta-salas">${salas || '<li class="cinza">Você ainda não tem salas. Crie uma ou entre com um código: ela aparece aqui.</li>'}</ul></div>`;
}

/** Guarda (ou busca) o save do jogo solo na conta. */
export function salvarJogoNaConta(texto: string): void {
  if (!conta) return;
  void chamar('/conta/save', { token: conta.token, save: texto }).then(
    () => {
      temSaveNaConta = true;
      app.mensagem = 'Jogo salvo na sua conta.';
      ganchos.desenhar();
    },
    falha,
  );
}

export function carregarJogoDaConta(aoReceber: (texto: string) => void): void {
  if (!conta) return;
  void chamar<{ dados: { save: string } | null }>(`/conta/save?token=${encodeURIComponent(conta.token)}`).then((r) => {
    if (!r.dados) app.mensagem = 'Nenhum jogo salvo na sua conta.';
    else aoReceber(r.dados.save);
    ganchos.desenhar();
  }, falha);
}

export function acaoOnline(nome: string, d: DOMStringMap = {}): void {
  app.mensagem = '';
  if (nome === 'online-conta-entrar' || nome === 'online-conta-registrar') {
    const rota = nome === 'online-conta-entrar' ? 'entrar' : 'registrar';
    const usuarioDigitado = campo('conta-usuario');
    void entrarNaConta(rota, usuarioDigitado, (document.getElementById('conta-senha') as HTMLInputElement | null)?.value ?? '', campo('conta-email')).then((estado) => {
      app.mensagem = estado === 'confirmar' ? 'Enviamos um código de 6 dígitos para o seu e-mail: digite abaixo.' : `Bem-vindo, ${sessaoDaConta()?.usuario ?? usuarioDigitado}!`;
      ganchos.desenhar();
    }, (erro) => {
      falha(erro);
      ganchos.desenhar();
    });
    return;
  }
  if (nome === 'online-conta-sair') {
    const antigo = conta;
    gravarConta(null);
    salasDaConta = [];
    if (antigo) void chamar('/conta/sair', { token: antigo.token }).catch(() => undefined);
    return;
  }
  if (nome === 'online-conta-sala') {
    void conectar(d.codigo ?? '', d.token ?? '').then(() => ganchos.desenhar(), falha);
    return;
  }
  if (nome === 'online-menu') {
    app.tela = 'online';
    if (aba === 'salas') aba = 'criar';
    if (prefill.codigo) aba = 'entrar'; // veio por um link de sala: abre direto na aba de entrar
    tardia = null;
    pararEspera();
    void Promise.all([carregarCampeonatos(), carregarConta()]).then(() => ganchos.desenhar());
    return;
  }
  if (nome === 'online-voltar') {
    pararAtualizadorSalas();
    pararEspera();
    tardia = null;
    app.tela = 'inicio';
    return;
  }
  if (nome === 'online-aba') {
    guardarCamposDaTela();
    aba = d.p === 'salas' ? 'salas' : d.p === 'entrar' ? 'entrar' : d.p === 'conta' ? 'conta' : 'criar';
    if (aba === 'conta') void carregarConta().then(() => ganchos.desenhar()); // atualiza "minhas salas"
    if (aba === 'salas') {
      void carregarSalas().then(() => ganchos.desenhar());
      ligarAtualizadorSalas();
    } else pararAtualizadorSalas();
    return;
  }
  if (nome === 'online-sala-entrar') {
    // clicou numa sala da lista: volta para a aba de entrada com o código preenchido (e a senha, se houver, a digitar)
    prefill.codigo = (d.codigo ?? '').toUpperCase();
    prefill.senhaEntrar = '';
    aba = 'entrar';
    pararAtualizadorSalas();
    ganchos.desenhar();
    window.setTimeout(() => (document.getElementById(d.senha === '1' ? 'online-senha' : 'online-nome') as HTMLInputElement | null)?.focus(), 80);
    return;
  }
  if (nome === 'online-tardia-voltar') {
    tardia = null;
    return;
  }
  if (nome === 'online-espera-cancelar') {
    pararEspera();
    return;
  }
  if (nome === 'online-pedir' && tardia) {
    const t = tardia;
    const clube = t.clubes.find((c) => c.id === Number(d.id));
    if (!clube) return;
    void chamar<{ pedido: string }>(`/salas/${encodeURIComponent(t.codigo)}/pedir`, { nome: t.nome, conta: conta?.token, senha: t.senha, clubeId: clube.id })
      .then((r) => {
        tardia = null;
        acompanharPedido(t.codigo, r.pedido, clube.nome);
        ganchos.desenhar();
      }, falha);
    return;
  }
  if (nome === 'online-criar') {
    prefill.nome = campo('online-nome');
    prefill.prazo = campo('online-prazo') || '0';
    prefill.campeonato = campo('online-camp') || 'copa-do-brasil';
    prefill.prazoPers = campo('online-pers') || prefill.prazoPers;
    const prazoMin = prazoEmMinutos();
    if (prazoMin === null) {
      app.mensagem = 'Prazo personalizado: digite de 1 minuto a 24 horas.';
      ganchos.desenhar();
      return;
    }
    prefill.senhaCriar = campo('online-senha-criar');
    prefill.maxTecnicos = campo('online-max') || prefill.maxTecnicos;
    void chamar<{ codigo: string; token: string }>('/salas', { nome: prefill.nome, prazoMin, campeonato: prefill.campeonato, conta: conta?.token, senha: prefill.senhaCriar, maxTecnicos: Number(prefill.maxTecnicos) })
      .then((r) => conectar(r.codigo, r.token))
      .then(() => ganchos.desenhar(), falha);
    return;
  }
  if (nome === 'online-entrar') {
    prefill.nome = campo('online-nome');
    prefill.codigo = campo('online-codigo').toUpperCase();
    prefill.senhaEntrar = campo('online-senha');
    const codigoSala = prefill.codigo;
    const nomeTecnico = prefill.nome;
    const senhaSala = prefill.senhaEntrar;
    void chamar<{ token: string } | { emAndamento: true; clubes: ClubeLivre[] }>(`/salas/${encodeURIComponent(codigoSala)}/entrar`, { nome: nomeTecnico, conta: conta?.token, senha: senhaSala })
      .then(async (r) => {
        if ('emAndamento' in r) {
          // campeonato já iniciado: escolhe um clube da CPU e pede ao anfitrião
          if (r.clubes.length === 0) throw new Error('Não há clubes livres nesta sala.');
          tardia = { codigo: codigoSala, nome: nomeTecnico, senha: senhaSala, clubes: r.clubes };
          return;
        }
        await conectar(codigoSala, r.token);
      })
      .then(() => ganchos.desenhar(), falha);
    return;
  }
  if (nome === 'online-reconectar') {
    const s = lerSessao();
    if (!s) return;
    void conectar(s.codigo, s.token).then(() => ganchos.desenhar(), (erro) => {
      // só esquece a sala se o servidor disse que ela (ou o token) não existe; queda de rede não apaga a sessão
      if (/token|sala/i.test(erroTexto(erro))) gravarSessao(null);
      app.online = null;
      falha(erro);
    });
    return;
  }
  const o = app.online;
  if (!o) return;
  if (nome === 'online-sair') return sairOnline();
  if (nome === 'online-pedidos') {
    app.dialogo = 'pedidos';
    return;
  }
  if (nome === 'online-pedido-resp') {
    void chamar<VisaoSala>(`/salas/${o.codigo}/pedido-resposta`, { token: o.token, pedidoId: Number(d.id), aceitar: d.ok === '1' }).then((v) => {
      aplicarVisao(v);
      if (v.sala.pedidos.length === 0) app.dialogo = null;
      ganchos.desenhar();
    }, falha);
    return;
  }
  if (nome === 'online-iniciar') {
    void chamar<VisaoSala>(`/salas/${o.codigo}/iniciar`, { token: o.token }).then((v) => {
      aplicarVisao(v);
      ganchos.desenhar();
    }, falha);
  }
}

export function escolherClubeOnline(valor: string): void {
  const o = app.online;
  if (!o) return;
  void chamar<VisaoSala>(`/salas/${o.codigo}/clube`, { token: o.token, clubeId: valor === '' ? null : Number(valor) }).then((v) => {
    aplicarVisao(v);
    ganchos.desenhar();
  }, falha);
}

// ---- comandos durante o jogo ---------------------------------------------------------------------

export async function enviar(comando: Comando): Promise<RespostaComando> {
  const o = app.online;
  if (!o) throw new Error('sem sala online');
  const r = await chamar<{ visao: RespostaVisao; resposta: RespostaComando }>(`/salas/${o.codigo}/comando`, { token: o.token, comando, base: o.base ? o.vid : null });
  if (!receberVisao(r.visao)) await atualizar();
  return r.resposta;
}

function moeda(n: number): string {
  return n.toLocaleString('pt-BR');
}

/** Executa a ação pelo servidor quando ela mexe no estado do jogo. Devolve true se tratou a ação (o chamador não faz nada local). */
export function onlineAcao(nome: string, d: DOMStringMap): boolean {
  const o = app.online;
  if (!o || !o.visao?.sala.iniciada) return false;
  const e = jogoAtual();
  const rodar = (p: Promise<unknown>): true => {
    void p.then(() => ganchos.desenhar(), falha);
    return true;
  };

  if (nome === 'esc-voltar' || nome === 'esc-jogar') {
    if (!aplicarEscalar()) return true;
    const jogar = nome === 'esc-jogar';
    app.dialogo = null;
    app.escalar = null;
    return rodar(
      enviar({ tipo: 'tatica', tatica: humano().tatica })
        .then(() => (jogar ? enviar({ tipo: 'pronto', valor: true }) : undefined))
        .then(() => {
          app.mensagem = jogar ? 'Escalação enviada. Aguardando os outros técnicos...' : 'Escalação salva no servidor.';
        }),
    );
  }
  if (nome === 'emprego-aceitar' || nome === 'emprego-recusar') {
    const aceitar = nome === 'emprego-aceitar';
    return rodar(
      enviar({ tipo: 'emprego-responder', ofertaId: Number(d.id), aceitar }).then((r) => {
        app.mensagem = r.mensagem ?? (aceitar ? 'Você assumiu o clube.' : 'Proposta recusada.');
        // convite com o técnico empregado: fecha a janela quando não restam convites (ou quando aceitou e trocou de clube)
        if (app.dialogo === 'convites' && (aceitar || (app.online?.visao?.eu.convites.length ?? 0) <= 1)) app.dialogo = null;
      }, (erro) => {
        app.mensagem = `Não foi possível responder: ${erroTexto(erro)}.`;
      }),
    );
  }
  if (nome === 'pausar-jogo') return rodar(enviar({ tipo: 'pausar' }));
  if (nome === 'pausa-ok') {
    const st = app.pausa;
    if (!st) return true;
    const formacao = formacaoParaEnvio(st);
    const comando: Comando = { tipo: st.intervalo ? 'intervalo-confirmar' : 'intervir', subs: st.pendentes, estilo: st.estilo, marcacao: st.marcacao, ataque: st.ataque, passe: st.passe, pressao: st.pressao, ...(formacao ? { formacao } : {}) };
    app.dialogo = null;
    app.pausa = null;
    return rodar(enviar(comando));
  }
  if (nome === 'online-pronto') {
    const pronto = !!o.visao.sala.jogadores.find((j) => j.clubeId === o.visao?.eu.clubeId)?.pronto;
    return rodar(enviar({ tipo: 'pronto', valor: !pronto }));
  }
  if (nome === 'ate-fim') {
    app.mensagem = 'No modo online a temporada anda rodada a rodada.';
    return true;
  }
  if (nome === 'encerrar') {
    const campeao = textoCampeoes(e);
    const outros = o.visao.sala.jogadores.filter((j) => j.nome !== o.visao?.eu.nome).map((j) => j.nome);
    const aviso = `Encerrar a temporada?${campeao ? ` ${campeao}.` : ''}\n\nIsso começa uma nova temporada para TODOS os técnicos da sala${outros.length ? ` (${outros.join(', ')})` : ''}${e.copa ? ' e sorteia a copa de novo' : ''}.`;
    if (!window.confirm(aviso)) return true;
    return rodar(
      enviar({ tipo: 'encerrar' }).then(() => {
        app.dialogo = null;
        app.mensagem = 'Nova temporada começou.';
      }),
    );
  }
  if (nome === 'salvar') {
    app.mensagem = 'O servidor salva o campeonato sozinho.';
    return true;
  }
  if (nome === 'expandir') return rodar(enviar({ tipo: 'expandir' }).then((r) => void (app.mensagem = r.mensagem ?? 'Obra concluída.')));
  if (nome === 'emp-pegar' || nome === 'emp-pagar') return rodar(enviar({ tipo: 'emprestimo', acao: nome === 'emp-pegar' ? 'pegar' : 'pagar' }));
  if (nome === 'comprar') {
    const id = Number(d.id);
    const dono = clubePorId(e, Number(d.club));
    const j = dono.elenco.find((x) => x.id === id);
    if (!j) return true;
    return rodar(
      enviar({ tipo: 'comprar', jogadorId: id }).then((r) => {
        const p = r.proposta;
        app.mensagem = p?.tipo === 'aceita' ? `Contratação fechada: ${j.nome} por ${moeda(p.valor)}.` : `Não foi possível comprar: ${p?.tipo === 'recusada' ? p.motivo : 'o clube pediu outro valor'}.`;
        if (p?.tipo === 'aceita') {
          app.procura.sel = null;
          app.times.jogadorId = null;
        }
      }),
    );
  }
  if (nome === 'oferta-aceitar' || nome === 'oferta-recusar') {
    return rodar(enviar({ tipo: 'oferta-responder', ofertaId: Number(d.id), aceitar: nome === 'oferta-aceitar' }).then((r) => void (app.mensagem = r.mensagem ?? '')));
  }
  if (nome === 'oferta-cancelar') return rodar(enviar({ tipo: 'oferta-cancelar', ofertaId: Number(d.id) }).then((r) => void (app.mensagem = r.mensagem ?? '')));
  if (nome === 'proposta' && app.negociacao) {
    const neg = app.negociacao;
    const dono = e.clubes.find((c) => c.elenco.some((x) => x.id === neg.jogadorId));
    if (dono?.humano) {
      // jogador de outro técnico humano: a oferta vai para ele, que aceita ou recusa no menu Leilão
      return rodar(
        enviar({ tipo: 'oferta-humana', jogadorId: neg.jogadorId, valor: neg.oferta }).then((r) => {
          app.negociacao = null;
          app.mensagem = r.mensagem ?? 'Oferta enviada.';
        }),
      );
    }
    return rodar(
      enviar({ tipo: 'comprar', jogadorId: neg.jogadorId, oferta: neg.oferta }).then((r) => {
        if (r.proposta?.tipo === 'aceita') {
          app.mensagem = `Contratação fechada por ${moeda(r.proposta.valor)}.`;
          app.negociacao = null;
          app.procura.sel = null;
          app.times.jogadorId = null;
        } else neg.resposta = r.proposta ?? null;
      }),
    );
  }
  if (nome === 'aceitar-contra' && app.negociacao) {
    const neg = app.negociacao;
    return rodar(
      enviar({ tipo: 'aceitar-contra', jogadorId: neg.jogadorId, valor: Number(d.valor) }).then(() => {
        app.mensagem = `Contratação fechada por ${moeda(Number(d.valor))}.`;
        app.negociacao = null;
        app.procura.sel = null;
        app.times.jogadorId = null;
      }, (erro) => {
        app.mensagem = `Não foi possível fechar: ${erroTexto(erro)}.`;
      }),
    );
  }
  if (nome === 'comprar-externo') {
    return rodar(
      enviar({ tipo: 'comprar-externo', jogadorId: Number(d.id) }).then((r) => {
        app.mensagem = r.mensagem ?? 'Contratação fechada.';
        app.procura.sel = null;
      }, (erro) => {
        app.mensagem = `Não foi possível contratar: ${erroTexto(erro)}.`;
      }),
    );
  }
  if (nome === 'contratar-livre') {
    return rodar(
      enviar({ tipo: 'contratar-livre', jogadorId: Number(d.id) }).then(() => {
        app.mensagem = 'Jogador contratado como livre.';
        app.procura.sel = null;
      }, (erro) => {
        app.mensagem = `Não foi possível contratar: ${erroTexto(erro)}.`;
      }),
    );
  }
  if (nome === 'vender') {
    const id = Number(d.id);
    return rodar(enviar({ tipo: 'ofertas-venda', jogadorId: id }).then((r) => void (app.ofertasVenda = { jogadorId: id, ofertas: r.ofertas ?? [] })));
  }
  if (nome === 'vender-p') {
    return rodar(
      enviar({ tipo: 'vender', jogadorId: Number(d.id), compradorId: Number(d.comp), valor: Number(d.valor) }).then(() => {
        app.mensagem = `Venda fechada por ${moeda(Number(d.valor))}.`;
        app.ofertasVenda = null;
        app.jogadorSel = null;
      }, (erro) => {
        app.mensagem = `Não foi possível vender: ${erroTexto(erro)}.`;
      }),
    );
  }
  if (nome === 'jr-promover' || nome === 'jr-dispensar' || nome === 'jr-apelido' || nome === 'jr-comprar') {
    const me = humano();
    const alvoId = app.juniores.clubeId ?? me.id;
    const dono = clubePorId(e, alvoId);
    const j = (dono.juniores ?? []).find((x) => x.id === app.juniores.jogadorId) ?? (dono.juniores ?? [])[0];
    if (!j) return true;
    if (nome === 'jr-dispensar' && !window.confirm(`Deseja dispensar o junior ${j.nome}?`)) return true;
    const acao = nome === 'jr-promover' ? 'promover' : nome === 'jr-dispensar' ? 'dispensar' : nome === 'jr-apelido' ? 'apelido' : 'comprar';
    return rodar(
      enviar({ tipo: 'junior', acao, jogadorId: j.id, apelido: app.juniores.apelido, clubeId: dono.id }).then(() => {
        if (nome === 'jr-apelido') app.juniores.apelido = '';
        else app.juniores.jogadorId = null;
        app.mensagem = 'Feito.';
      }, (erro) => {
        app.mensagem = `Não foi possível: ${erroTexto(erro)}.`;
      }),
    );
  }
  if (nome === 'leilao-criar') {
    const id = Number(d.id);
    const j = humano().elenco.find((x) => x.id === id);
    const preco = Number(app.leilao.preco) || (j ? Math.round(j.valor / 1000) * 1000 : 0);
    return rodar(
      enviar({ tipo: 'leilao-criar', jogadorId: id, precoInicial: preco, duracaoMin: Number(app.leilao.duracao) }).then(() => {
        app.ofertasVenda = null;
        app.leilao.preco = '';
        app.dialogo = 'leilao';
        app.mensagem = 'Leilão aberto. Os outros técnicos já podem dar lances.';
      }, (erro) => {
        app.mensagem = `Não foi possível abrir o leilão: ${erroTexto(erro)}.`;
      }),
    );
  }
  if (nome === 'leilao-lance') {
    const id = Number(d.id);
    const l = o.visao.sala.leiloes.find((x) => x.id === id);
    if (!l) return true;
    const valor = Number(app.leilao.valores[id] ?? lanceMinimo(l));
    return rodar(
      enviar({ tipo: 'leilao-lance', leilaoId: id, valor }).then(() => {
        delete app.leilao.valores[id];
        app.mensagem = 'Lance registrado. Você lidera o leilão.';
      }, (erro) => {
        app.mensagem = `Lance recusado: ${erroTexto(erro)}.`;
      }),
    );
  }
  if (nome === 'leilao-cancelar') {
    return rodar(
      enviar({ tipo: 'leilao-cancelar', leilaoId: Number(d.id) }).then(() => {
        app.mensagem = 'Leilão cancelado.';
      }, (erro) => {
        app.mensagem = `Não foi possível cancelar: ${erroTexto(erro)}.`;
      }),
    );
  }
  return false;
}

/** Preços do estádio digitados no modo online vão para o servidor. */
export function enviarPrecos(): void {
  const c = humano();
  void enviar({ tipo: 'precos', sugeridos: c.usarPrecosSugeridos, precos: [...c.precos] }).then(() => ganchos.desenhar(), falha);
}

// ---- telas ---------------------------------------------------------------------------------------

function abasOnline(): string {
  const b = (id: 'criar' | 'entrar' | 'salas' | 'conta', rotulo: string): string => `<button class="btn ${aba === id ? 'principal' : ''}" data-a="online-aba" data-p="${id}" role="tab" aria-selected="${aba === id}">${rotulo}</button>`;
  return `<div class="dlg-botoes" role="tablist" style="justify-content:flex-start;margin:6px 0">${b('criar', 'Criar sala')}${b('entrar', 'Entrar na sala')}${b('salas', 'Salas')}${b('conta', conta ? `Conta (${esc(conta.usuario)})` : 'Conta')}</div>`;
}

function textoEstadoSala(s: ResumoSala): string {
  if (!s.iniciada) return 'aguardando técnicos';
  return `temporada ${s.temporada} · rodada ${s.rodada}/${s.rodadasTotal}`;
}

/** Aba "Salas": todas as salas do servidor, com vagas e se pedem senha. */
function telaSalas(): string {
  const lista = salasPublicas ?? [];
  const linhas = lista
    .map((s) => {
      const cheia = s.iniciada ? (s.vagas ?? 0) === 0 : s.tecnicos >= s.maxTecnicos;
      const acao = cheia
        ? '<span class="cinza peq">sem vagas</span>'
        : `<button class="btn peq ${s.iniciada ? '' : 'principal'}" data-a="online-sala-entrar" data-codigo="${esc(s.codigo)}" data-senha="${s.temSenha ? '1' : '0'}">${s.iniciada ? 'Pedir vaga' : 'Entrar'}</button>`;
      const vagas = s.iniciada ? `${s.vagas ?? 0} clube(s) livre(s)` : `${s.tecnicos}/${s.maxTecnicos} técnicos`;
      return `<tr><td class="esq"><b>${esc(s.codigo)}</b>${s.temSenha ? ' <span title="Esta sala tem senha" aria-label="com senha">🔒</span>' : ''}</td><td class="esq">${esc(s.campeonato)}</td><td class="esq">${esc(s.anfitriao)}</td>
        <td>${esc(textoEstadoSala(s))}</td><td>${s.tecnicos}/${s.maxTecnicos}</td><td>${esc(vagas)}</td><td>${acao}</td></tr>`;
    })
    .join('');
  const corpo =
    salasPublicas === null
      ? '<p class="cinza">Carregando as salas…</p>'
      : lista.length === 0
        ? '<p class="cinza">Nenhuma sala criada ainda. Vá em "Criar sala" para abrir a primeira.</p>'
        : `<div class="rolavel"><table class="tab-dlg"><thead><tr><th class="esq">Sala</th><th class="esq">Campeonato</th><th class="esq">Anfitrião</th><th>Estado</th><th>Técnicos</th><th>Vagas</th><th></th></tr></thead><tbody>${linhas}</tbody></table></div>`;
  return `<div class="bf-novo"><div class="novo-painel" style="max-width:1000px">
    <h1 class="marca">WebFoot · Online</h1>
    <h2>Salas</h2>
    <div class="novo-form">
      ${abasOnline()}
      ${corpo}
      <p class="cinza peq">A lista se atualiza sozinha. Em campeonato já iniciado você escolhe um clube da CPU e o anfitrião precisa aprovar a sua entrada.</p>
      ${app.mensagem ? `<div class="aviso-bf dentro" role="alert">${esc(app.mensagem)}</div>` : ''}
      <hr />
      <div class="dlg-botoes"><button class="btn" data-a="online-voltar">« Voltar</button><button class="btn" data-a="online-aba" data-p="salas">Atualizar</button></div>
    </div></div></div>`;
}

/** Técnico demitido: espera propostas de clubes da CPU (a cada rodada há chance de chegar uma). */
export function telaDemitido(): string {
  const v = app.online?.visao;
  const emp = v?.eu.emprego;
  if (!v || !emp) return '';
  const aoVivo = Boolean(v.sala.aoVivo);
  const grupo = (c: { divisao: 1 | 2 | null; liga?: string; ligaNome?: string }): string => (c.liga ? (c.ligaNome ?? c.liga) : c.divisao === 1 ? 'Série A' : c.divisao === 2 ? 'Série B' : 'Clube');
  const linhas = emp.propostas
    .map(
      ({ ofertaId, clube: c }) => `<tr><td class="esq">${escudo(c)}${esc(c.nome)}</td><td>${esc(grupo(c))}</td><td>${c.posicao !== null ? `${c.posicao}º` : '–'}</td><td>${c.pontos ?? '–'}</td><td class="esq">${c.copa ? esc(c.copa) : '–'}</td>
        <td><button class="btn peq principal" data-a="emprego-aceitar" data-id="${ofertaId}" ${aoVivo ? 'disabled' : ''}>Aceitar</button> <button class="btn peq" data-a="emprego-recusar" data-id="${ofertaId}" ${aoVivo ? 'disabled' : ''}>Recusar</button></td></tr>`,
    )
    .join('');
  const tabela = linhas
    ? `<h3 class="fase-tit">Propostas de emprego</h3><table class="tab-dlg"><thead><tr><th class="esq">Clube</th><th>Divisão</th><th>Posição</th><th>Pontos</th><th class="esq">Copa do Brasil</th><th></th></tr></thead><tbody>${linhas}</tbody></table>`
    : '<p class="cinza">Nenhuma proposta no momento. A cada rodada que passa há chance de um clube te chamar.</p>';
  return `<div class="bf-novo"><div class="novo-painel" style="max-width:900px">
    <h1 class="marca">Sala <span class="codigo-sala">${esc(v.sala.codigo)}</span></h1>
    <h2>Você foi demitido${emp.demitidoDe ? ` do ${esc(emp.demitidoDe)}` : ''}</h2>
    <div class="novo-form">
      <p>A confiança da diretoria ou da torcida chegou a 10%. Enquanto você espera, o campeonato continua. Aceite uma proposta para voltar a comandar um time (o clube vem do jeito que está) ou espere a próxima.</p>
      <div class="rolavel">${tabela}</div>
      ${aoVivo ? '<p class="cinza peq">Há uma rodada em andamento: responda às propostas depois do apito final.</p>' : ''}
      ${app.mensagem ? `<div class="aviso-bf dentro" role="alert">${esc(app.mensagem)}</div>` : ''}
    </div></div></div>`;
}

/** Técnico empregado com convites por desempenho: aceitar troca de clube (o antigo vira da CPU); recusar mantém tudo como está. */
export function dlgConvites(): string {
  const v = app.online?.visao;
  const lista = v?.eu.convites ?? [];
  const aoVivo = Boolean(v?.sala.aoVivo);
  const grupo = (c: { divisao: 1 | 2 | null; liga?: string; ligaNome?: string; pais?: string }): string => (c.liga ? `${c.ligaNome ?? c.liga} · ${c.pais ?? ''}` : c.divisao === 1 ? 'Série A' : c.divisao === 2 ? 'Série B' : 'Clube');
  const linhas = lista
    .map(
      ({ ofertaId, clube: c }) => `<tr><td class="esq">${escudo(c)}${esc(c.nome)}</td><td class="esq">${esc(grupo(c))}</td><td>${c.posicao !== null ? `${c.posicao}º` : '–'}</td><td>${c.pontos ?? '–'}</td>
        <td><button class="btn peq principal" data-a="emprego-aceitar" data-id="${ofertaId}" ${aoVivo ? 'disabled' : ''}>Aceitar</button> <button class="btn peq" data-a="emprego-recusar" data-id="${ofertaId}" ${aoVivo ? 'disabled' : ''}>Recusar</button></td></tr>`,
    )
    .join('');
  return dialogo(
    'Convites de emprego',
    `<div class="dlg-tit amarelo">Convites de emprego</div>
    <div class="dlg-corpo">
      <p>Pelo desempenho na última temporada, estes clubes querem você como técnico. Se aceitar, você assume o clube do jeito que está (com a diretoria e a torcida em 70%) e o seu clube atual passa para a CPU. Os convites valem por algumas rodadas.</p>
      ${linhas ? `<div class="rolavel"><table class="tab-dlg"><thead><tr><th class="esq">Clube</th><th class="esq">Liga</th><th>Posição</th><th>Pontos</th><th></th></tr></thead><tbody>${linhas}</tbody></table></div>` : '<p class="cinza">Nenhum convite no momento.</p>'}
      ${aoVivo ? '<p class="cinza peq">Há uma rodada em andamento: responda depois do apito final.</p>' : ''}
      ${app.mensagem ? `<div class="aviso-bf dentro" role="alert">${esc(app.mensagem)}</div>` : ''}
    </div>
    <div class="dlg-botoes"><button class="btn" data-a="fechar">Decidir depois</button></div>`,
    'medio',
  );
}

/** Sala já iniciada: o técnico novo escolhe qual clube da CPU quer assumir. */
function telaTardia(): string {
  const t = tardia;
  if (!t) return '';
  // grupos: Série A e B (e "clubes livres", numa sala de copa) e uma tabela por liga estrangeira (sala com o mundo inteiro)
  const chaveDe = (c: (typeof t.clubes)[number]): string => c.liga ?? String(c.divisao);
  const grupo = (chave: string): string => {
    const c = t.clubes.find((x) => chaveDe(x) === chave);
    return c?.liga ? `${c.ligaNome ?? c.liga} · ${c.pais ?? ''}` : chave === '1' ? 'Série A' : chave === '2' ? 'Série B' : 'Clubes livres';
  };
  const divisoes = [...new Set(t.clubes.map(chaveDe))];
  const tabelas = divisoes
    .map((d) => {
      const linhas = t.clubes
        .filter((c) => chaveDe(c) === d)
        .map(
          (c) => `<tr><td class="esq">${escudo(c)}${esc(c.nome)}</td><td>${c.posicao !== null ? `${c.posicao}º` : '–'}</td><td>${c.pontos ?? '–'}</td><td class="esq">${c.copa ? esc(c.copa) : '–'}</td>
            <td><button class="btn peq principal" data-a="online-pedir" data-id="${c.id}">Pedir este clube</button></td></tr>`,
        )
        .join('');
      return `<h3 class="fase-tit">${grupo(d)}</h3><table class="tab-dlg"><thead><tr><th class="esq">Clube</th><th>Posição</th><th>Pontos</th><th class="esq">Copa do Brasil</th><th></th></tr></thead><tbody>${linhas}</tbody></table>`;
    })
    .join('');
  return `<div class="bf-novo"><div class="novo-painel" style="max-width:900px">
    <h1 class="marca">Sala <span class="codigo-sala">${esc(t.codigo)}</span></h1>
    <h2>Campeonato em andamento: escolha um clube</h2>
    <div class="novo-form">
      <p>Você entra como <b>${esc(t.nome)}</b> assumindo um clube que hoje é jogado pela CPU. O clube vem do jeito que está (elenco, caixa e posição) e o <b>anfitrião precisa aprovar</b>.</p>
      <div class="rolavel">${tabelas}</div>
      ${app.mensagem ? `<div class="aviso-bf dentro" role="alert">${esc(app.mensagem)}</div>` : ''}
      <hr />
      <div class="dlg-botoes"><button class="btn" data-a="online-tardia-voltar">« Voltar</button></div>
    </div></div></div>`;
}

/** Pedido enviado: espera a resposta do anfitrião. */
function telaEspera(): string {
  const w = espera;
  if (!w) return '';
  return `<div class="bf-novo"><div class="novo-painel">
    <h1 class="marca">Sala <span class="codigo-sala">${esc(w.codigo)}</span></h1>
    <h2>Aguardando o anfitrião…</h2>
    <div class="novo-form">
      <p>Você pediu para assumir o <b>${esc(w.clube)}</b>. Assim que o anfitrião aprovar (e a rodada que estiver rolando terminar), você entra automaticamente.</p>
      <p class="cinza peq">Pode deixar esta tela aberta: ela confere a resposta a cada 2 segundos.</p>
      <hr />
      <div class="dlg-botoes"><button class="btn" data-a="online-espera-cancelar">Cancelar pedido</button></div>
    </div></div></div>`;
}

/** Aba "Conta": login, cadastro e as salas da conta (separada de criar e entrar numa sala). */
function telaConta(): string {
  return `<div class="bf-novo"><div class="novo-painel">
    <h1 class="marca">WebFoot · Online</h1>
    <h2>Conta</h2>
    <div class="novo-form">
      ${abasOnline()}
      ${blocoConta()}
      <p class="cinza peq">A conta é obrigatória para jogar online. Com ela você reencontra as suas salas de qualquer aparelho e guarda o save do jogo solo na nuvem.</p>
      ${app.mensagem ? `<div class="aviso-bf dentro" role="alert">${esc(app.mensagem)}</div>` : ''}
      <hr />
      <div class="dlg-botoes"><button class="btn" data-a="online-voltar">« Voltar</button></div>
    </div></div></div>`;
}

/** Sem conta logada não dá para jogar online: só o login/cadastro (a conta é obrigatória). */
function telaLoginObrigatorio(): string {
  return `<div class="bf-novo"><div class="novo-painel">
    <h1 class="marca">WebFoot · Online</h1>
    <h2>Entre na sua conta</h2>
    <div class="novo-form">
      <p>Para jogar online você precisa estar logado. Entre ou crie uma conta (é gratuita).</p>
      ${blocoConta()}
      ${app.mensagem ? `<div class="aviso-bf dentro" role="alert">${esc(app.mensagem)}</div>` : ''}
      <hr />
      <div class="dlg-botoes"><button class="btn" data-a="online-voltar">« Voltar</button></div>
    </div></div></div>`;
}

export function telaEntrada(): string {
  if (!conta) return telaLoginObrigatorio();
  if (espera) return telaEspera();
  if (tardia) return telaTardia();
  if (aba === 'salas') return telaSalas();
  if (aba === 'conta') return telaConta();
  const salva = lerSessao();
  const prazos: [string, string][] = [['0', 'Sem prazo (todos confirmam)'], ['5', '5 minutos'], ['10', '10 minutos'], ['60', '1 hora'], ['1440', '24 horas'], ['custom', 'Personalizado…']];
  const opcoes = prazos.map(([v, t]) => `<option value="${v}" ${prefill.prazo === v ? 'selected' : ''}>${t}</option>`).join('');
  // só os campeonatos que a sala aceita criar hoje (Série A e Série B soltas já fazem parte do "A + B + Copa")
  const todos: InfoCampeonato[] = campeonatos.length > 0 ? campeonatos : [{ id: 'copa-do-brasil', nome: 'Copa do Brasil', disponivel: true, descricao: '' }];
  const lista = todos.filter((c) => c.disponivel);
  if (!lista.some((c) => c.id === prefill.campeonato)) prefill.campeonato = lista[0]?.id ?? 'copa-do-brasil';
  const campOpcoes = lista.map((c) => `<option value="${c.id}" ${prefill.campeonato === c.id ? 'selected' : ''}>${esc(c.nome)}</option>`).join('');
  const campDescricao = lista.find((c) => c.id === prefill.campeonato)?.descricao ?? '';
  const prazoPersonalizado =
    prefill.prazo === 'custom'
      ? `<label>Prazo personalizado: <input type="number" id="online-pers" data-ch="online-pers" min="1" max="1440" step="1" inputmode="numeric" value="${esc(prefill.prazoPers)}" style="width:5em" />
          <select id="online-unid" data-ch="online-unid"><option value="min" ${prefill.prazoUnidade === 'h' ? '' : 'selected'}>minutos</option><option value="h" ${prefill.prazoUnidade === 'h' ? 'selected' : ''}>horas</option></select>
          <span class="cinza peq">(de 1 minuto a 24 horas)</span></label>`
      : '';
  return `<div class="bf-novo"><div class="novo-painel">
    <h1 class="marca">WebFoot · Online</h1>
    <h2>Campeonato com amigos</h2>
    <div class="novo-form">
      ${abasOnline()}
      <p class="cinza peq">${conta ? `Jogando com a conta <b>${esc(conta.usuario)}</b>.` : 'Jogando sem conta (opcional: use a aba Conta para entrar ou criar uma).'}</p>
      <hr />
      <label>Seu nome de técnico: <input type="text" id="online-nome" maxlength="24" value="${esc(prefill.nome)}" autocomplete="off" /></label>
      <hr />
      ${
        aba === 'criar'
          ? `<label>Campeonato: <select id="online-camp" data-ch="online-camp">${campOpcoes}</select></label>
      <p class="cinza peq camp-desc">${logoCampeonato(prefill.campeonato, 40)}<span>${esc(campDescricao)}</span></p>
      <label>Prazo por rodada: <select id="online-prazo" data-ch="online-prazo">${opcoes}</select></label>
      ${prazoPersonalizado}
      <label>Máximo de técnicos: <select id="online-max" data-ch="online-max">${Array.from({ length: 19 }, (_, i) => i + 2).map((n) => `<option value="${n}" ${String(n) === prefill.maxTecnicos ? 'selected' : ''}>${n}</option>`).join('')}</select></label>
      <label>Senha da sala (opcional): <input type="password" id="online-senha-criar" maxlength="40" value="${esc(prefill.senhaCriar)}" autocomplete="new-password" placeholder="sem senha = qualquer pessoa com o código entra" /></label>
      <p class="cinza peq">Cada técnico monta o time quando quiser. A rodada roda quando todos confirmam ou no fim do prazo, usando a escalação salva.</p>`
          : `<label>Código da sala: <input type="text" id="online-codigo" maxlength="8" value="${esc(prefill.codigo)}" autocomplete="off" placeholder="ex.: K7M2Q" /></label>
      <label>Senha da sala (se tiver): <input type="password" id="online-senha" maxlength="40" value="${esc(prefill.senhaEntrar)}" autocomplete="off" placeholder="deixe em branco se a sala não tem senha" /></label>
      <p class="cinza peq">Não sabe o código? Veja as salas abertas na aba <b>Salas</b>. Se o campeonato já começou, você escolhe um clube da CPU e o anfitrião aprova a sua entrada.</p>`
      }
      ${app.mensagem ? `<div class="aviso-bf dentro" role="alert">${esc(app.mensagem)}</div>` : ''}
      <hr />
      <div class="dlg-botoes">
        <button class="btn" data-a="online-voltar">« Voltar</button>
        ${salva ? `<button class="btn" data-a="online-reconectar">Reconectar (${esc(salva.codigo)})</button>` : ''}
        ${aba === 'criar' ? '<button class="btn principal" data-a="online-criar">Criar sala</button>' : '<button class="btn principal" data-a="online-entrar">Entrar na sala</button>'}
      </div>
    </div></div></div>`;
}

export function telaLobby(): string {
  const o = app.online;
  const v = o?.visao;
  if (!o || !v) return '<div class="bf-novo"><div class="novo-painel"><p>Conectando...</p></div></div>';
  const ocupados = new Set(v.sala.jogadores.map((j) => j.clubeId).filter((x): x is number => x !== null));
  const clubes = [...v.estado.clubes].filter((c) => v.sala.clubesDisponiveis.includes(c.id)).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  const linhas = v.sala.jogadores
    .map((j) => {
      const eu = j.nome === v.eu.nome;
      const clube = j.clubeId === null ? null : clubes.find((c) => c.id === j.clubeId);
      const celula = eu
        ? `<select data-ch="online-clube" aria-label="Seu clube"><option value="">Escolha um clube</option>${(() => {
            const opcao = (c: (typeof clubes)[number]): string => `<option value="${c.id}" ${c.id === j.clubeId ? 'selected' : ''}>${esc(c.nome)}</option>`;
            const livres = clubes.filter((c) => !ocupados.has(c.id) || c.id === j.clubeId);
            // com Série A e Série B na sala, os clubes aparecem separados por divisão
            if (!v.estado.acesso) return livres.map(opcao).join('');
            // sala com o mundo inteiro: depois das duas divisões brasileiras, uma lista por liga estrangeira
            const grupos: { rotulo: string; vale: (c: (typeof clubes)[number]) => boolean }[] = [
              { rotulo: 'Série A', vale: (c) => !c.liga && c.divisao === 1 },
              { rotulo: 'Série B', vale: (c) => !c.liga && c.divisao === 2 },
              ...PAISES_MUNDO.flatMap((p) => p.ligas.map((l) => ({ rotulo: `${l.nome} · ${p.nome}`, vale: (c: (typeof clubes)[number]) => c.liga === l.id }))),
            ];
            return grupos
              .map((g) => ({ g, itens: livres.filter(g.vale) }))
              .filter(({ itens }) => itens.length > 0)
              .map(({ g, itens }) => `<optgroup label="${esc(g.rotulo)}">${itens.map(opcao).join('')}</optgroup>`)
              .join('');
          })()}</select>`
        : clube
          ? `${escudo(clube)}${esc(clube.nome)}`
          : '<span class="cinza">escolhendo...</span>';
      return `<tr class="${eu ? 'humano' : ''}"><td class="esq">${esc(j.nome)}${j.host ? ' <span class="cinza">(anfitrião)</span>' : ''}${eu ? ' <b>(você)</b>' : ''}</td><td class="esq">${celula}</td></tr>`;
    })
    .join('');
  const todos = v.sala.jogadores.every((j) => j.clubeId !== null);
  const link = `${location.origin}${location.pathname}?sala=${v.sala.codigo}`;
  return `<div class="bf-novo"><div class="novo-painel">
    <h1 class="marca">Sala <span class="codigo-sala">${esc(v.sala.codigo)}</span></h1>
    <h2>${logoCampeonato(v.sala.campeonato, 32)}${esc(v.sala.nomeCampeonato)} · aguardando os técnicos</h2>
    <div class="novo-form">
      <p>Chame os amigos pelo link: <input class="link-sala" type="text" readonly value="${esc(link)}" onclick="this.select()" aria-label="Link da sala" /></p>
      <table class="tab-dlg"><thead><tr><th class="esq">Técnico</th><th class="esq">Clube</th></tr></thead><tbody>${linhas}</tbody></table>
      <p class="cinza peq">Prazo por rodada: ${v.sala.prazoMin > 0 ? `${v.sala.prazoMin} min` : 'sem prazo'}. Os clubes sem técnico humano ficam com a CPU.</p>
      ${app.mensagem ? `<div class="aviso-bf dentro" role="alert">${esc(app.mensagem)}</div>` : ''}
      <hr />
      <div class="dlg-botoes"><button class="btn" data-a="online-sair">Sair da sala</button>
        ${v.eu.host ? `<button class="btn principal" data-a="online-iniciar" ${todos ? '' : 'disabled'}>Iniciar campeonato »</button>` : '<span class="cinza">Só o anfitrião inicia.</span>'}</div>
    </div></div></div>`;
}

/** Faixa no alto do jogo com a sala, os técnicos (✓ = confirmou a rodada) e o botão de confirmar. */
export function barraOnline(): string {
  const o = app.online;
  const v = o?.visao;
  if (!o || !v) return '';
  const e = jogoAtual();
  const eu = v.sala.jogadores.find((j) => j.clubeId === v.eu.clubeId);
  const lista = v.sala.jogadores
    .map((j) => {
      const c = j.clubeId === null ? null : clubePorId(e, j.clubeId);
      const marca = j.joga ? (j.pronto ? '✓' : '…') : 'fora';
      return `<span class="on-tec ${j.pronto ? 'pronto' : ''}${j.joga ? '' : ' fora'}" title="${j.joga ? (j.pronto ? 'confirmou a rodada' : 'ainda montando o time') : 'não joga esta rodada'}">${c ? escudo(c) : ''}${esc(j.nome)} ${marca}</span>`;
    })
    .join('');
  const fim = temporadaAcabou(e);
  const prazo = v.sala.prazoEm ? ` · roda às ${new Date(v.sala.prazoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : '';
  const jogo = !fim && !!eu?.joga;
  const campeoes = fim ? textoCampeoes(e) : '';
  const fimTexto = fim ? `<span class="on-fim">${esc(e.formato === 'copa' ? 'Copa encerrada' : 'Temporada encerrada')}${campeoes ? ` · <b>${esc(campeoes)}</b>` : ''}</span>` : '';
  const botao = fim
    ? v.eu.host
      ? '<button class="btn principal peq" data-a="encerrar">Encerrar temporada</button>'
      : '<span class="cinza">Aguardando o anfitrião encerrar a temporada</span>'
    : jogo
      ? `<button class="btn ${eu?.pronto ? '' : 'principal'} peq" data-a="online-pronto">${eu?.pronto ? 'Desmarcar confirmação' : 'Confirmar rodada'}</button>`
      : '<span class="cinza">Você não joga esta rodada: aguarde os outros técnicos</span>';
  // só o anfitrião vê: técnicos que pediram para entrar no campeonato em andamento
  const pedidos = v.sala.pedidos.length > 0 ? `<button class="btn peq principal" data-a="online-pedidos" title="Técnicos que querem entrar na sala">🙋 Pedidos de entrada (${v.sala.pedidos.length})</button>` : '';
  return `<div class="on-barra" role="status"><span class="on-sala">Sala <b>${esc(v.sala.codigo)}</b>${prazo}</span><span class="on-lista">${lista}</span>${fimTexto}${pedidos}${botao}<button class="btn peq" data-a="online-sair">Sair</button></div>`;
}

// ---- cronômetro dos leilões (atualiza só o texto, sem redesenhar a tela) ---------------------------

let ultimaAtualizacaoFim = 0;
setInterval(() => {
  const o = app.online;
  const v = o?.visao;
  if (!o || !v) return;
  const agora = agoraDoServidor();
  let vencido = false;
  document.querySelectorAll<HTMLElement>('[data-fim]').forEach((el) => {
    const resto = Number(el.dataset.fim) - agora;
    el.textContent = formatarRestante(resto);
    if (resto <= 0) vencido = true;
  });
  if (vencido && Date.now() - ultimaAtualizacaoFim > 4000) {
    ultimaAtualizacaoFim = Date.now();
    void atualizar();
  }
}, 1000);